interface Feature {
  /** A mono label, as you'd name the thing in code. */
  label: string;
  description: string;
  /** One concrete line a developer would type or see. */
  example?: string;
  pro?: boolean;
}

/*
 * A spec sheet, not a grid of icon tiles (docs/brand.md → Iconography): a mono label, what
 * it does, and an example of it.
 */
const FEATURES: Feature[] = [
  {
    label: 'endpoints',
    description:
      'Create custom API endpoints that return exactly what you need. Set status codes, headers, response bodies, and simulated delays.',
    example: 'GET /api/users/:id → 200 application/json',
  },
  {
    label: 'request log',
    description:
      'Watch HTTP requests stream in live via WebSocket. Inspect headers, body, query parameters, and response timing as they happen.',
  },
  {
    label: 'templates',
    description:
      'Fill response bodies from the request and built-in generators, so one endpoint can answer many requests realistically.',
    example: '{ "id": "{{$uuid}}", "event": "{{request.body.event}}" }',
  },
  {
    label: 'actions',
    description:
      'Build automated pipelines that trigger when requests hit your endpoints. Forward to webhooks, transform responses with templates, store variables across requests, and chain actions with conditional logic.',
    pro: true,
  },
  {
    label: 'subdomains',
    description:
      'Every project gets its own subdomain. Share clean, memorable URLs with your team or paste them into configs.',
    example: 'https://acme-payments.mockd.sh',
  },
  {
    label: 'analytics',
    description:
      'Track request volume, status code distribution, response times, and usage patterns with a built-in analytics dashboard per endpoint.',
  },
  {
    label: 'infrastructure',
    description:
      'Globally distributed edge network — always fast, zero servers to manage. Just create and start sending requests.',
  },
];

export function Features() {
  return (
    <section className="py-20 bg-base-100 border-t border-base-300">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl tracking-tight">Everything you need to mock, test, and debug</h2>
        <p className="mt-3 text-lg text-base-content/70 max-w-2xl">
          A complete toolkit for API mocking and webhook testing — no server setup, no config files.
        </p>

        <dl className="mt-12 border-b border-base-300">
          {FEATURES.map((feature) => (
            <div
              key={feature.label}
              className="grid grid-cols-1 md:grid-cols-[12rem_minmax(0,1fr)] gap-x-8 gap-y-2 py-6 border-t border-base-300"
            >
              <dt className="font-mono text-sm font-medium text-base-content flex items-center gap-2 h-fit">
                {feature.label}
                {feature.pro && (
                  <span className="font-mono text-xs font-medium bg-primary/10 text-primary px-1.5 py-0.5 rounded-selector">
                    pro
                  </span>
                )}
              </dt>
              <dd className="space-y-3">
                <p className="text-base-content/80 leading-relaxed max-w-2xl">{feature.description}</p>
                {feature.example && (
                  <code className="inline-block max-w-full font-mono text-sm text-base-content code-surface px-2.5 py-1 overflow-x-auto whitespace-pre">
                    {feature.example}
                  </code>
                )}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}
